import {
  resolveAccountMachineState,
  selectSyncSurfaceActionAvailability,
  selectSyncSurfaceActionDescription,
  type AccountCloudPosture,
  type AccountSnapshot,
  type SyncSurfaceAction,
  type SyncSurfaceActionAvailability
} from './accountStateMachine.js'

const INDEX_PALETTE_ACTIONS = new Set<SyncSurfaceAction>([
  'export sync shelf',
  'index-export-all',
  'space-export-file',
  'space-import-file',
  'space-open-file',
  'space-new',
  'space-rename',
  'space-delete-!'
])

const VERIFIED_CLOUD_REQUIRED_INDEX_ACTIONS = new Set<SyncSurfaceAction>([
  'space-rename'
])

export function isSpaceManagementAction(action: string): boolean {
  return INDEX_PALETTE_ACTIONS.has(action as SyncSurfaceAction)
}

export function selectSyncPaletteActionAvailability(snapshot: AccountSnapshot): SyncSurfaceActionAvailability[] {
  return selectSyncSurfaceActionAvailability(snapshot)
    .filter(entry => !isSpaceManagementAction(entry.action))
}

export function selectIndexPaletteActionAvailability(snapshot: AccountSnapshot): SyncSurfaceActionAvailability[] {
  const lifecycle = resolveAccountMachineState(snapshot)
  if (lifecycle === 'sync_deactivated') {
    return [
      { action: 'index-export-all', state: 'armed' },
      { action: 'space-export-file', state: 'armed' }
    ]
  }
  const canMutateShelf = !snapshot.isViewMode
  const canExportShelf = !snapshot.isViewMode

  return [
    { action: 'index-export-all', state: canExportShelf ? 'armed' : 'disabled' },
    { action: 'space-export-file', state: 'armed' },
    { action: 'space-import-file', state: canMutateShelf ? 'armed' : 'disabled' },
    { action: 'space-open-file', state: canMutateShelf ? 'armed' : 'disabled' },
    { action: 'space-new', state: canMutateShelf ? 'armed' : 'disabled' },
    { action: 'space-rename', state: canMutateShelf ? 'armed' : 'disabled' },
    { action: 'space-delete-!', state: canMutateShelf ? 'armed' : 'disabled' }
  ]
}

export function applyCloudPosturePaletteActionOverlay<T extends SyncSurfaceActionAvailability>(
  actions: readonly T[],
  cloudPosture: AccountCloudPosture
): T[] {
  if (cloudPosture !== 'offline-unverified') return [...actions]
  return actions.map(entry => (
    VERIFIED_CLOUD_REQUIRED_INDEX_ACTIONS.has(entry.action)
      ? { ...entry, state: 'disabled' as const }
      : entry
  ))
}

export function selectPaletteActionDescription(snapshot: AccountSnapshot, action: string): string | undefined {
  if (action === 'index-export-all') {
    return 'Export spaces in memory as standard JSON files in one zip.'
  }
  if (action === 'space-export-file') {
    return 'Export the current space as a portable JSON file.'
  }
  if (action === 'space-import-file') {
    return 'Import a local JSON space file into the current space.'
  }
  if (action === 'space-open-file') {
    return 'Open a local JSON space file and replace the current space.'
  }
  return selectSyncSurfaceActionDescription(snapshot, action as SyncSurfaceAction)
}
